import useResizeObserver from "@react-hook/resize-observer";
import { RefObject, useLayoutEffect, useState } from "react";

/**
 * Custom hook for measuring element dimensions and tracking size changes.
 *
 * Features:
 * - Measures element dimensions using getBoundingClientRect
 * - Tracks size changes using ResizeObserver
 * - Provides real-time size updates
 * - Handles initial size measurement on mount
 * - Returns DOMRectReadOnly for comprehensive size information
 * - Automatically cleans up observer on unmount
 * - Uses useLayoutEffect for immediate initial measurement
 *
 * @param target - Reference to the element to measure
 * @returns Current element size as DOMRectReadOnly or undefined
 */
export const useSize = (target: RefObject<HTMLElement>) => {
  const [size, setSize] = useState<DOMRectReadOnly>();

  useLayoutEffect(() => {
    setSize(target.current?.getBoundingClientRect());
  }, [target]);

  useResizeObserver(target, (entry) => setSize(entry.contentRect));
  return size;
};

export default useSize;
